import { ESessionErrorCode } from '@/constants';
import { reportCustomEvent } from '@/utils/arms';

import { hasSessionReadyCookie } from './session-ready-cookie';
import { clearSessionRecoveryPending, markSessionRecoveryPending, readSessionExpiresAt } from './session-storage';

interface IAccountOperationResult {
    /** 服务端账号请求及同步身份更新是否成功。 */
    accountSucceeded: boolean;
    /** 本次响应有效期是否已读回确认落盘。 */
    expiryPersisted: boolean;
}

// 冷启动等待超时，与 login / logout 的请求超时一致。
const SESSION_BOOTSTRAP_TIMEOUT_MS = 30_000;
// 剩余有效期大于 0 且不超过 10 天时，本轮才允许静默换发。
const SILENT_REFRESH_WINDOW_MS = 10 * 24 * 60 * 60 * 1000;
// 鉴权请求全部结束后再空闲这么久，才发起一次静默 login。
const SILENT_IDLE_MS = 10_000;

// 冷启动入口；fetch 不直接导入 Auth，避免循环依赖。
let bootstrapRunner: (() => Promise<void>) | null = null;
// 只执行会话 login，供过期强刷、静默换发和 401 恢复复用。
let loginRunner: ((forceRefresh: boolean) => Promise<void>) | null = null;
// 读取当前进行中的会话 login，账号操作开始时先等它结束。
let getLoginInflight: (() => Promise<void> | null) | null = null;
// 正式登录或登出进行中的账号操作；业务请求等它结束后再判断会话。
let accountOperationPromise: Promise<IAccountOperationResult> | null = null;
// 本页已建立会话的版本；请求发送时快照，用于识别身份切换前的旧鉴权响应。
let sessionVersion = 0;
// 落盘失败时阻止继续使用旧有效期或自动重复登录，直到后续显式账号操作成功。
let sessionExpiryPersisted = true;
// 本轮静默换发任务；进行中的鉴权请求等待它结束。
let silentPromise: Promise<void> | null = null;
// 合并同批鉴权错误的恢复任务，避免多次 login 和多次刷新。
let recoveryPromise: Promise<void> | null = null;
// 空闲静默换发的定时器；有鉴权请求或账号操作时清除。
let silentTimer: ReturnType<typeof setTimeout> | null = null;
// 本页是否已做过冷启静默资格判断；Strict Mode 重跑不重置。
let silentStarted = false;
// 冷启时是否处于静默窗口；无本地有效期时先标为可判断，login 成功后再按真实有效期决定。
let silentEligible = false;
// 本轮是否已尝试过静默换发；成功、失败或超时后都不再试。
let silentAttempted = false;
// 正式登录或登出开始后禁止本页再次安排静默换发，直到整页刷新。
let silentDisabled = false;
// 正在发送的鉴权请求数；大于 0 时不发起静默换发。
let authRequestsInflight = 0;
// 最近一次鉴权请求结束时间，用于计算是否已空闲满 SILENT_IDLE_MS。
let lastAuthRequestEndedAt = 0;

function clearSilentTimer(): void {
    if (silentTimer !== null) {
        clearTimeout(silentTimer);
        silentTimer = null;
    }
}

/** 由启动模块注册，fetch 不直接导入 AuthApi，避免循环依赖。 */
export function registerSessionBootstrapRunner(runner: () => Promise<void>): void {
    bootstrapRunner = runner;
}

export function registerSessionLoginRunner(
    runner: (forceRefresh: boolean) => Promise<void>,
    inflight: () => Promise<void> | null
): void {
    loginRunner = runner;
    getLoginInflight = inflight;
}

export function hasAccountOperation(): boolean {
    return accountOperationPromise !== null;
}

/** 可能连续有账号操作；始终等到最后一项结束，返回其实际结果。 */
export async function waitForAccountOperation(): Promise<IAccountOperationResult | null> {
    let result: IAccountOperationResult | null = null;
    let operation = accountOperationPromise;
    while (operation) {
        result = await operation;
        operation = accountOperationPromise;
    }
    return result;
}

/** 仅在服务端已建立会话时推进版本；落盘结果独立于账号请求结果。 */
export function recordSessionEstablished(expiryPersisted: boolean): void {
    sessionVersion += 1;
    sessionExpiryPersisted = expiryPersisted;
}

export function getSessionVersion(): number {
    return sessionVersion;
}

/** 同步占位后才允许等待旧 login；旧 login 不反向等待后来的账号操作。 */
export function beginAccountOperation() {
    silentDisabled = true;
    clearSilentTimer();
    const previousAccount = accountOperationPromise;
    const previousLogin = getLoginInflight?.();
    let finished = false;
    let complete: (result: IAccountOperationResult) => void;
    const operation = new Promise<IAccountOperationResult>(resolve => {
        complete = resolve;
    });
    accountOperationPromise = operation;

    return {
        async waitForPrevious(): Promise<void> {
            await previousAccount;
            // 旧会话 login 失败不阻止用户用正式凭证登录或登出。
            await previousLogin?.catch(() => undefined);
        },
        finish(result: IAccountOperationResult): void {
            if (finished) {
                return;
            }
            finished = true;
            if (result.accountSucceeded) {
                recordSessionEstablished(result.expiryPersisted);
            }
            if (accountOperationPromise === operation) {
                accountOperationPromise = null;
            }
            complete(result);
        },
    };
}

function hasValidSession(): boolean {
    const expiresAt = readSessionExpiresAt();
    return sessionExpiryPersisted && expiresAt !== null && expiresAt > Date.now();
}

function runSessionLogin(forceRefresh: boolean): Promise<void> {
    if (!loginRunner) {
        return Promise.reject(new Error(ESessionErrorCode.SESSION_LOGIN_NOT_REGISTERED));
    }
    return loginRunner(forceRefresh);
}

async function waitForBootstrap(): Promise<void> {
    if (!bootstrapRunner) {
        throw new Error(ESessionErrorCode.SESSION_BOOTSTRAP_NOT_REGISTERED);
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            bootstrapRunner(),
            new Promise<never>((_, reject) => {
                timer = setTimeout(
                    () => reject(new Error(ESessionErrorCode.SESSION_BOOTSTRAP_TIMEOUT)),
                    SESSION_BOOTSTRAP_TIMEOUT_MS
                );
            }),
        ]);
    } finally {
        clearTimeout(timer);
    }
}

/**
 * 同步返回当前必须等待的任务。调用方在每次 await 后重查；返回 null 后可立即登记并发送。
 * 无本地有效期且无 session_ready 时，鉴权请求等待冷启动，与新用户同一条路径。
 */
export function getSessionRequestWait(requiresAuth: boolean): Promise<void> | null {
    if (!requiresAuth) {
        return null;
    }
    if (accountOperationPromise) {
        return accountOperationPromise.then(() => undefined);
    }
    if (!sessionExpiryPersisted) {
        return Promise.reject(new Error(ESessionErrorCode.SESSION_EXPIRY_NOT_PERSISTED));
    }
    if (silentPromise) {
        return silentPromise;
    }
    const expiresAt = readSessionExpiresAt();
    if (expiresAt !== null) {
        if (expiresAt > Date.now()) {
            return null;
        }
        return runSessionLogin(true).then(() => {
            if (!hasValidSession()) {
                throw new Error(ESessionErrorCode.SESSION_EXPIRY_NOT_PERSISTED);
            }
        });
    }
    return hasSessionReadyCookie() ? null : waitForBootstrap();
}

function isInSilentWindow(): boolean {
    const expiresAt = readSessionExpiresAt();
    if (expiresAt === null) {
        return false;
    }
    const remaining = expiresAt - Date.now();
    return remaining > 0 && remaining <= SILENT_REFRESH_WINDOW_MS;
}

function reportSessionFailure(reason: string): void {
    reportCustomEvent('SessionRecoveryFailed', reason, { properties: { reason } });
}

async function trySilentRefresh(): Promise<void> {
    if (silentDisabled || silentAttempted || !silentEligible || !isInSilentWindow() || authRequestsInflight > 0) {
        return;
    }
    const login = getLoginInflight?.();
    if (login) {
        await login.catch(() => undefined);
        return;
    }
    if (Date.now() - lastAuthRequestEndedAt < SILENT_IDLE_MS) {
        return;
    }
    // 占位与 login 发出之间不 await，之后进入的业务请求会等待这次静默任务。
    silentAttempted = true;
    silentPromise = runSessionLogin(true).catch(() => {
        reportSessionFailure(ESessionErrorCode.SILENT_LOGIN_FAILED);
    });
    try {
        await silentPromise;
    } finally {
        silentPromise = null;
    }
}

function scheduleSilentRefresh(): void {
    clearSilentTimer();
    if (
        !silentStarted ||
        silentDisabled ||
        silentAttempted ||
        !silentEligible ||
        !sessionExpiryPersisted ||
        !isInSilentWindow() ||
        authRequestsInflight > 0
    ) {
        return;
    }
    silentTimer = setTimeout(
        () => {
            silentTimer = null;
            trySilentRefresh()
                .catch(() => reportSessionFailure(ESessionErrorCode.SILENT_REFRESH_FAILED))
                .finally(() => {
                    if (!silentAttempted) {
                        scheduleSilentRefresh();
                    }
                });
        },
        Math.max(0, SILENT_IDLE_MS - (Date.now() - lastAuthRequestEndedAt))
    );
}

/** 只判断本轮资格，不安排未来数天后进入换发窗口的调度。Strict Mode 重跑不重置。 */
export function startSessionSilentRefresh(): void {
    if (silentStarted) {
        return;
    }
    silentStarted = true;
    lastAuthRequestEndedAt = Date.now();
    const expiresAt = readSessionExpiresAt();
    silentEligible = expiresAt === null || isInSilentWindow();
    scheduleSilentRefresh();
}

/** 无本地记录时，冷启 login 成功后按真实有效期重新判断本轮空闲机会。 */
export function notifySessionLoginCompleted(): void {
    scheduleSilentRefresh();
}

/** 只统计实际网络过程；调用方负责同步登记后立即调用 Axios。 */
export function beginAuthenticatedRequest(): () => void {
    authRequestsInflight += 1;
    clearSilentTimer();
    return () => {
        authRequestsInflight -= 1;
        lastAuthRequestEndedAt = Date.now();
        scheduleSilentRefresh();
    };
}

async function recoverSession(requestSessionVersion: number): Promise<void> {
    if (!markSessionRecoveryPending()) {
        throw new Error(ESessionErrorCode.SESSION_RECOVERY_MARK_NOT_PERSISTED);
    }
    const accountResult = await waitForAccountOperation();
    if (!sessionExpiryPersisted) {
        throw new Error(ESessionErrorCode.SESSION_EXPIRY_NOT_PERSISTED);
    }
    const sessionAlreadyRecovered = requestSessionVersion < sessionVersion || accountResult?.accountSucceeded === true;
    if (!sessionAlreadyRecovered || !hasValidSession()) {
        await runSessionLogin(true);
    }
    // login 期间可能开始正式登录或 logout，刷新前必须等账号请求与同步状态切换结束。
    let latestAccountResult = await waitForAccountOperation();
    while (hasAccountOperation() || latestAccountResult?.accountSucceeded === false) {
        if (!sessionExpiryPersisted) {
            throw new Error(ESessionErrorCode.SESSION_EXPIRY_NOT_PERSISTED);
        }
        if (latestAccountResult?.accountSucceeded === false) {
            await runSessionLogin(true);
        }
        latestAccountResult = await waitForAccountOperation();
    }
    if (!hasValidSession()) {
        throw new Error(ESessionErrorCode.SESSION_EXPIRY_NOT_PERSISTED);
    }
    if (!clearSessionRecoveryPending() || !hasValidSession()) {
        throw new Error(ESessionErrorCode.SESSION_RECOVERY_MARK_NOT_CLEARED);
    }
    reportCustomEvent('SessionRecoveryReload', 'auth_recovered', { properties: { reason: 'business_auth_error' } });
    window.location.reload();
}

/** 业务请求仍 reject，不重放；成功任务保留到页面导航，合并同批晚返回的旧鉴权错误。 */
export function recoverSessionFromAuthError(requestSessionVersion = getSessionVersion()): Promise<void> {
    if (!recoveryPromise) {
        recoveryPromise = recoverSession(requestSessionVersion).catch(error => {
            recoveryPromise = null;
            reportSessionFailure(error instanceof Error ? error.message : ESessionErrorCode.SESSION_RECOVERY_FAILED);
        });
    }
    return recoveryPromise;
}
